import axios from "axios"
import React, { useEffect, useState } from "react"
import GetCSFR from "../../hooks/userAuthentication/GetCSFR"
import CoachScreeningDocument from "../../components/general/admin-preview/CoachScreeningDocument"
import NewsletterTool from "../../components/general/admin-preview/NewsletterTool"
import ClubManagementTool from "../../components/general/admin-preview/ClubManagementTool"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRotateRight } from "@fortawesome/free-solid-svg-icons"

interface Data {
    monthlyRevenue: number;
    totalSoldServices: number;
    totalAthletes: number;
    totalCoaches: number;
    activeUsers: number;
    weeklyUsers: number;
    unreviewedScreenings: number;
    monthlyAthletes: number,
    monthlyCoaches: number;
}
 
export default function Admin() {
    const [validated, setValidated] = useState<boolean>(false)
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const [data, setData] = useState<Data>({
        monthlyRevenue: 0,
        totalSoldServices: 0,
        totalAthletes: 0,
        totalCoaches: 0,
        activeUsers: 0,
        weeklyUsers: 0,
        unreviewedScreenings: 0,
        monthlyAthletes: 0,
        monthlyCoaches: 0
    })
    const [password, setPassword] = useState<string>("")
    const [documentAccessPassword, setDocumentAccessPassword] = useState<string>("")
    const [documentAccessStatus, setDocumentAccessStatus] = useState<boolean>(false)
    const correctPassword = process.env.REACT_APP_ADMIN_LOGIN


    useEffect(()=>{
        const timer = setTimeout(() => {
            // Force reload the page after 10 minutes as security precaution
            window.location.reload()
        }, 600000)
        return ()=> clearTimeout(timer)
    },[])

    function retrieveData(event: React.FormEvent) {
        event.preventDefault()
        
        if (password !== correctPassword) {
            alert(`Incorrect password`)
            return
        }
        else {
            setValidated(true)

            // API call to get number of active users in last 30 minutes
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/administration/get_active_users/`, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            })
            .then(res => {
                if (res.status === 200) {
                    setData(prev => ({ ...prev, activeUsers: res.data.activeUsers }))
                }
            })
            .catch(error => console.error("Error retrieving active users", error))

            // API call to get number of weekly visitors from Google Analytics
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/administration/get_weekly_visitors/`, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            })
            .then(res => {
                if (res.status === 200) {
                    setData(prev => ({ ...prev, weeklyUsers: res.data.weeklyUsers }))
                }
            })
            .catch(error => console.error("Error retrieving weekly visitors", error))

            // API call to get number of monthly signups
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/administration/get_monthly_and_total_signups/`, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            })
            .then(res => {
                if (res.status === 200) {
                    setData(prev => ({ 
                        ...prev, 
                        totalAthletes: res.data.totalAthletes, 
                        totalCoaches: res.data.totalCoaches,
                        monthlyAthletes: res.data.monthlyAthletes,
                        monthlyCoaches: res.data.monthlyCoaches
                    }));
                }
            })
            .catch(error => console.error("Error retrieving monthly signups", error))

            // API call to get monthly revenue from Stripe
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/administration/get_monthly_revenue/`, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            })
            .then(res => {
                if (res.status === 200) {
                    setData(prev => ({ ...prev, monthlyRevenue: res.data.monthlyRevenue, totalSoldServices: res.data.totalSoldServices }))
                }
            })
            .catch(error => console.error("Error retrieving monthly revenue", error))

            // API call to get coach screening documents that need to be reviewed
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/administration/get_unreviewed_coach_screenings/`, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            })
            .then(res => {
                if (res.status === 200) {
                    setData(prev => ({ ...prev, unreviewedScreenings: res.data.totalUnreviewedDocuments }))
                }
            })
            .catch(error => console.error("Error retrieving monthly revenue", error))
        }
    }

    // Ensure user has priviledges to view screening documents
    function verifyDocumentAccess(){
        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/does_user_exist/`, {email: documentAccessPassword}, {
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json',
        },
        withCredentials: true,
        })
        .then(res => {
            if (res.status === 200 && res.data.exists) {
                setDocumentAccessStatus(true)
            } 
            else {
                setDocumentAccessStatus(false)
            }
        })
        .catch(console.error)
    }

    return (
        <div className="my-10 mx-auto">
            {!validated ? (
                <form className="flex flex-col justify-center mt-60 mx-auto" onSubmit={retrieveData}>
                    <input 
                        type="password" 
                        className="my-4 form-input w-72 mx-auto px-3"
                        autoComplete="off"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button type="submit" className="form-btn mx-auto py-2 px-6">
                        Sign In
                    </button>
                </form>
            ) : (
                <div className="flex flex-wrap justify-start m-2 py-4 px-10">
                    <div className="flex w-full mb-10 md:mb-16">
                        <h1 className="underline text-xl md:text-3xl font-kulim ml-0 mr-auto my-auto">Admin Dashboard</h1>
                        <button 
                            className="form-btn rounded-lg ml-auto mr-0 my-auto text-xs md:text-sm px-2 md:px-4 py-2" 
                            onClick={(event)=>retrieveData(event)}
                        >   
                            Refresh
                            <FontAwesomeIcon icon={faRotateRight} className="ml-2 text-white"/>
                        </button>
                    </div>
                    <div className="flex flex-wrap lg:justify-center w-full">
                        <section className="grid grid-cols-2 gap-3 mb-10 font-kulim md:max-w-md xl:max-w-xl">
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4">Total Monthly Revenue</h1>
                                <p className="text-2xl">${data?.monthlyRevenue || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4"># of Services sold this month</h1>
                                <p className="text-2xl">{data?.totalSoldServices || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4"># of Vistors (last 7 days)</h1>
                                <p className="text-2xl">{data?.weeklyUsers || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4"># of Vistors (last 30 minutes)</h1>
                                <p className="text-2xl">{data?.activeUsers || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4"># of Athlete sign ups (last 30 days)</h1>
                                <p className="text-2xl">{data?.monthlyAthletes || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4"># of Coach sign ups (last 30 days)</h1>
                                <p className="text-2xl">{data?.monthlyCoaches || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4">Total # of Athletes</h1>
                                <p className="text-2xl">{data?.totalAthletes || 0}</p>
                            </div>
                            <div className="rounded-xl border border-main-grey-100 p-3 bg-white">
                                <h1 className="mb-4">Total # of Coaches</h1>
                                <p className="text-2xl">{data?.totalCoaches || 0}</p>
                            </div>
                        </section>
                        <div className="w-full h-full max-h-1 lg:max-h-full bg-main-grey-200 rounded-full lg:max-w-1 mt-2 mb-8 lg:m-auto"></div>
                        { !documentAccessStatus ?
                            <section className="flex flex-col justify-center items-center w-full font-kulim mx-auto lg:mr-0 md:max-w-md lg:max-w-xl bg-white border border-main-grey-100 rounded-xl px-4 py-24">
                                <h3 className="text-3xl lg:text-5xl mx-auto font-semibold mt-2">
                                    {data.unreviewedScreenings}
                                </h3>
                                <p className="mx-auto mt-6 lg:mt-4 mb-4 text-lg text-center">Coach Screening Documents Need Review</p>
                                <input
                                    type="email"
                                    placeholder="Enter your company email"
                                    className="form-input rounded-lg w-60 p-2.5 mb-10 lg:mb-0"
                                    onChange={(e)=>setDocumentAccessPassword(e.target.value)}
                                    value={documentAccessPassword}
                                />
                                <button 
                                    className={`form-btn rounded-lg m-auto text-sm md:text-base px-6 py-2 ${documentAccessPassword.length > 5 && documentAccessPassword.includes('@') ? 'cursor-pointer bg-main-green-500' : 'cursor-not-allowed bg-main-grey-200'} `} 
                                    onClick={()=>verifyDocumentAccess()}
                                    disabled={documentAccessPassword.length < 5 && !documentAccessPassword.includes('@')}
                                >
                                    View
                                </button>
                            </section>
                        :   
                            <section className="flex flex-col justify-center items-center w-full font-kulim mx-auto lg:mr-0 md:max-w-md lg:max-w-xl bg-white border border-main-grey-100 rounded-xl px-5 py-3">
                                <CoachScreeningDocument csrfToken={csrfToken} />
                            </section>
                        }
                    </div>
                    <div className="w-full">
                        <NewsletterTool csrfToken={csrfToken}/>
                    </div>
                    <ClubManagementTool />
                </div>
            )}
        </div>
    )
}
